import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays,
  Activity, Award, CalendarOff, ChartLine, Check, ChevronRight, CircleCheck, Clock, CreditCard, Folder, FolderKanban,
  IdCard, Lock, MailQuestion, MessagesSquare, Network, Newspaper, Percent, Plane, Receipt, FileText, Repeat,
  Send, Signature, Target, type LucideIcon,
} from 'lucide-react';
import { Icon } from '../design/Icon';
import { btnPrimary, card, cardLink, sectionTitle } from '../design/ui';
import { useSharedTodos } from '../notifications/TodosContext';
import { greetingFor, quoteFor, QUOTE_PERIOD_MS } from './homeFlavor';
import { valueStyle } from '../drivers/Trophy';
import { AwardCelebration } from '../drivers/AwardCelebration';
import { useHubMoments } from '../home/moments';
import { DriversPulse } from '../home/DriversPulseCard';
import { MomentsList } from '../home/MomentsSection';
import { TipLine, useTodayTip } from '../home/TipCard';
import { SetupPrompts } from '../home/SetupPrompts';
import { QuickActions } from '../home/QuickActions';
import { BalancesCard } from '../home/BalancesCard';
import { useMyRequests, type MyRequestStatus } from '../home/useMyRequests';
import { AnnouncementsCard } from '../home/AnnouncementsCard';
import { openProfileOnboarding, useProfileOnboarding } from '../home/ProfileOnboardingModal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useProfile } from '../profile/ProfileProvider';
import {
  TOOLS,
  TOOL_SLUGS,
  CALLABLES,
  isAnalyticsAdmin,
  visibleAnalyticsPages,
  isSubscriptionAdmin,
  visibleSubscriptionPages,
  isOperationsAdmin,
  visibleOperationsPages,
  type ToolMetadata,
  type ToolSlug,
  type HubRequestAccessRequest,
  type HubRequestAccessResponse,
  type HubAcknowledgeTodoInput,
  type HubAcknowledgeTodoResponse,
} from '@daaily/shared';

const callAcknowledgeTodo = httpsCallable<HubAcknowledgeTodoInput, HubAcknowledgeTodoResponse>(
  functions,
  CALLABLES.HUB_ACKNOWLEDGE_TODO,
);

/**
 * Hub landing page (design system v1): "your day" first, then the tools.
 * Two columns on lg+ (simplified Sep 2026): the working column on the left,
 * one quieter "This month" card on the right. On phones it stacks in DOM
 * order: greeting, to-dos, This month, tools.
 *
 *   1. Greeting + one-line summary, and the values quote as one italic line.
 *   2. Needs your attention: the same live to-dos the bell shows (approvals,
 *      drafts, onboarding tasks, signatures), each linking into the item.
 *   3. This month: DAAily Drivers pulse, Moments (winners with claps, joiners,
 *      anniversaries) and the daily tip, as rows in one card.
 *   4. Your tools: compact icon tiles (Lucide, same icons as the sidebar), no
 *      status badge unless a tool is not in its normal state.
 *   5. Coming soon: dimmed teaser tiles for inactive tools.
 *
 * If the user has no provisioned tools, the Request access tile leads the grid.
 */

/** Lucide per tool slug — the same mapping the sidebar uses. */
const TOOL_ICONS: Record<string, LucideIcon> = {
  expenses: Receipt, travel: Plane, invoices: FileText, drivers: Award, creditCards: CreditCard,
  absence: CalendarOff, documents: Folder, orgChart: Network, timeTracking: Clock,
  performanceReviews: MessagesSquare, commissions: Percent, usl: Target, subscriptions: Repeat,
  analytics: ChartLine, signatures: Signature, projects: FolderKanban, operations: Activity,
  outreach: Send, businessCard: IdCard, newsletter: Newspaper, directors: CalendarDays,
};

/** The values quote under the greeting, one quiet line; re-renders when the 3-hour slot turns. */
function ValueQuoteLine() {
  const [q, setQ] = useState(() => quoteFor());
  useEffect(() => {
    const next = QUOTE_PERIOD_MS - (Date.now() % QUOTE_PERIOD_MS) + 1000;
    const t = window.setTimeout(() => setQ(quoteFor()), next);
    return () => window.clearTimeout(t);
  }, [q]);
  const { color, emblem } = valueStyle(q.valueId);
  return (
    <figure className="mt-3 flex items-start gap-2 max-w-2xl">
      <span className="mt-0.5" style={{ color: `rgb(var(${color}))` }} title={q.value}>
        <Icon as={emblem} size={14} label={q.value} />
      </span>
      <blockquote className="text-sm italic text-fg-muted leading-6">
        &ldquo;{q.text}&rdquo; <span className="not-italic text-fg-faint">&middot; {q.by}</span>
      </blockquote>
    </figure>
  );
}

export function HubLandingPage() {
  const { state: profileState } = useProfile();
  const navigate = useNavigate();

  // What tools is the user provisioned for? Active tools only — coming-soon
  // tiles render regardless of provisioning since they're just teasers.
  const provisionedActive = useMemo<ToolMetadata[]>(() => {
    if (profileState.status !== 'ready') return [];
    const tools = profileState.user.tools ?? {};
    return TOOL_SLUGS
      .map((slug) => TOOLS[slug])
      .filter((meta) => {
        if (!meta.active) return false;
        const entry = tools[meta.slug as keyof typeof tools];
        if (entry?.enabled !== true) return false;
        // Page-gated tools (company-wide bulk enable): the tile shows only when
        // the user can actually SEE at least one page — same rule as the sidebar
        // (enabled-but-nothing-granted → hidden). Admins always see the tile.
        const u = profileState.user;
        const appAdmin = profileState.isAdmin;
        if (meta.slug === 'analytics') {
          return visibleAnalyticsPages(
            isAnalyticsAdmin({ isAppAdmin: appAdmin, role: u.tools?.analytics?.role }),
            u.analyticsPages ?? [],
          ).length > 0;
        }
        if (meta.slug === 'operations') {
          return visibleOperationsPages(
            isOperationsAdmin({ isAppAdmin: appAdmin, role: u.tools?.operations?.role }),
            u.operationsPages ?? [],
          ).length > 0;
        }
        if (meta.slug === 'subscriptions') {
          return visibleSubscriptionPages(
            isSubscriptionAdmin({ isAppAdmin: appAdmin, role: u.tools?.subscriptions?.role }),
            u.subscriptionPages ?? [],
          ).length > 0;
        }
        return true;
      });
  }, [profileState]);

  const comingSoon = useMemo<ToolMetadata[]>(() => {
    // Internal tools are NEVER public teasers — they appear only to provisioned
    // users (via provisionedActive), so exclude them from the coming-soon row.
    return TOOL_SLUGS.map((s) => TOOLS[s]).filter((meta) => !meta.active && !meta.internal);
  }, []);

  // /config/app.toolIcons (admin-uploaded tile images) is deliberately NOT used
  // here any more: the design system gives every tool the same Lucide icon as
  // the sidebar, so the home page reads as one set. The config field stays.
  // Tile-label overrides — same pattern. `applyLabelOverride` below merges
  // the registry's default name/description with admin-set values.
  const labelOverrides: Record<
    string,
    { name?: string; description?: string } | null
  > = useMemo(() => {
    if (profileState.status !== 'ready') return {};
    return profileState.config.toolLabels ?? {};
  }, [profileState]);

  const displayName =
    profileState.status === 'ready'
      ? profileState.user.displayName
      : 'there';

  const hasAnyAccess = provisionedActive.length > 0;
  const email = profileState.status === 'ready' ? profileState.user.email : '';
  const driversEnabled = profileState.status === 'ready' && profileState.user.tools?.drivers?.enabled === true;
  const moments = useHubMoments(email);
  const allTodos = useSharedTodos();
  // Acknowledge (Oct 2026): hide a to-do on HOME only — the bell and the tools
  // keep it. Stored on the user doc (hubAcknowledgeTodo); an item returns when
  // its label changes. `pendingAck` makes the click feel instant.
  const acked = profileState.status === 'ready' ? profileState.user.acknowledgedTodos ?? [] : [];
  const [pendingAck, setPendingAck] = useState<Record<string, boolean>>({});
  const [showAcked, setShowAcked] = useState(false);
  // Review surveys stay on Home until they are done (Oct 2026): no Got it for them.
  const isAcked = (t: { id: string; label: string; kind: string }) =>
    t.kind === 'review' ? false : (pendingAck[t.id] ?? acked.some((a) => a.id === t.id && a.sig === t.label));
  const todos = allTodos.filter((t) => !isAcked(t));
  const ackedTodos = allTodos.filter((t) => isAcked(t));
  const acknowledge = (t: { id: string; label: string }, undo = false) => {
    setPendingAck((m) => ({ ...m, [t.id]: !undo }));
    callAcknowledgeTodo({ id: t.id, label: t.label, undo }).catch(() => {
      setPendingAck((m) => { const n = { ...m }; delete n[t.id]; return n; });
    });
  };
  const summary = !hasAnyAccess
    ? "You don't have access to any tools yet. Request access from the tile below."
    : todos.length === 0
      ? 'Nothing needs you right now.'
      : `${todos.length} ${todos.length === 1 ? 'thing needs' : 'things need'} you.`;

  const pulse = moments?.pulse ?? null;
  const hasMoments = !!moments &&
    (moments.winners.length > 0 || moments.anniversaries.length > 0 || moments.joiners.length > 0 || (moments.starters?.length ?? 0) > 0);
  const { tip, dismiss: dismissTip, turnOff: turnTipsOff } = useTodayTip(email, driversEnabled);
  // The This month card's blocks, in order; only the ones with content render, so the card
  // never shows an empty heading and separators sit only between real blocks.
  const monthBlocks: { key: string; node: ReactNode }[] = [];
  if (pulse) monthBlocks.push({ key: 'pulse', node: <DriversPulse pulse={pulse} /> });
  if (moments && hasMoments) monthBlocks.push({ key: 'moments', node: <MomentsList data={moments} /> });
  if (tip) monthBlocks.push({ key: 'tip', node: <TipLine tip={tip} onDismiss={dismissTip} onTurnOff={turnTipsOff} /> });
  // Profile walk-through (Oct 2026): a permanent row while HR essentials are missing — opens the pop-up.
  const onboarding = useProfileOnboarding();
  const profileRow = onboarding.enabled && onboarding.status
    ? (!onboarding.status.complete
        ? (onboarding.status.missingMandatory.length > 0
            ? `Complete your profile: ${onboarding.status.missingMandatory.map((s) => s.label.toLowerCase()).join(', ')} missing`
            : `Complete your profile: ${onboarding.status.openOptional.map((s) => s.label.toLowerCase()).join(', ')} (optional)`)
        : onboarding.needsReview ? 'Check your details: address, private email, phone, children, emergency contact, bank details' : null)
    : null;
  const hasTodos = todos.length > 0 || ackedTodos.length > 0 || !!profileRow;
  // Your requests (Oct 2026): own submissions waiting for a decision + recent decisions.
  const myRequests = useMyRequests(profileState.status === 'ready' ? profileState.user : null);
  const hasRequests = myRequests.length > 0;
  // The right column spans every explicit row; Tailwind needs the literal class.
  const leftRows = (hasTodos ? 1 : 0) + (hasRequests ? 1 : 0);
  const gridRows = leftRows === 2 ? 'lg:grid-rows-[auto_auto_1fr]' : leftRows === 1 ? 'lg:grid-rows-[auto_1fr]' : 'lg:grid-rows-[1fr]';

  return (
    <main className="w-full max-w-page px-4 md:px-8 pt-6 pb-10 flex-1 space-y-8">
      <header className="min-w-0">
        <h1 className="font-sans text-3xl leading-9 font-semibold tracking-tighter text-body">
          {greetingFor(displayName.split(' ')[0] ?? displayName)}
        </h1>
        <p className="mt-1 text-sm italic text-muted">{summary}</p>
        <ValueQuoteLine />
      </header>

      {profileState.status === 'ready' && <QuickActions user={profileState.user} config={profileState.config} />}

      <AwardCelebration />

      <SetupPrompts />

      {profileState.status === 'ready' && <AnnouncementsCard user={profileState.user} />}

      <div className={`grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_21rem] ${gridRows} gap-8 items-start`}>
      {hasTodos && (
        <section aria-labelledby="attention-h" className="space-y-3 lg:col-start-1">
          <h2 id="attention-h" className={sectionTitle}>Needs your attention</h2>
          {(todos.length > 0 || profileRow) && (
            <ul className={`${card} divide-y divide-line overflow-hidden`}>
              {profileRow && (
                <li className="flex items-stretch">
                  <button type="button" onClick={openProfileOnboarding} className="flex items-center gap-3 px-4 py-3 min-w-0 flex-1 text-left hover:bg-surface-2 transition-colors duration-fast">
                    <span className="w-2 h-2 rounded-full bg-brand-solid shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-fg truncate">{profileRow}</span>
                      <span className="block text-xs text-fg-muted">Profile · a short walk-through</span>
                    </span>
                    <Icon as={ChevronRight} className="text-fg-faint" />
                  </button>
                </li>
              )}
              {todos.map((t) => (
                <li key={t.id} className="flex items-stretch">
                  <Link to={t.link} className="flex items-center gap-3 px-4 py-3 min-w-0 flex-1 hover:bg-surface-2 transition-colors duration-fast">
                    <span className="w-2 h-2 rounded-full bg-brand-solid shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-fg truncate">{t.label}</span>
                      <span className="block text-xs text-fg-muted">{t.tool}{t.subgroup ? ` · ${t.subgroup}` : ''}</span>
                    </span>
                    <Icon as={ChevronRight} className="text-fg-faint" />
                  </Link>
                  {t.kind !== 'review' && (
                    <button
                      type="button"
                      onClick={() => acknowledge(t)}
                      className="px-3 text-fg-faint hover:text-fg hover:bg-surface-2 transition-colors duration-fast border-l border-line"
                      title="Acknowledge: hide this here. It stays in the tool and the bell."
                      aria-label={`Acknowledge: ${t.label}`}
                    >
                      <Icon as={Check} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {ackedTodos.length > 0 && (
            <div className="text-xs text-fg-faint">
              {ackedTodos.length} acknowledged{' '}
              <button type="button" onClick={() => setShowAcked((v) => !v)} className="text-link hover:underline">
                {showAcked ? 'hide' : 'show'}
              </button>
              {showAcked && (
                <ul className={`${card} divide-y divide-line overflow-hidden mt-2 opacity-70`}>
                  {ackedTodos.map((t) => (
                    <li key={t.id} className="flex items-center gap-3 px-4 py-2">
                      <Link to={t.link} className="min-w-0 flex-1 hover:underline">
                        <span className="block text-sm text-fg truncate">{t.label}</span>
                        <span className="block text-xs text-fg-muted">{t.tool}</span>
                      </Link>
                      <button type="button" onClick={() => acknowledge(t, true)} className="text-xs text-link hover:underline">
                        Restore
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      {hasRequests && (
        <section aria-labelledby="requests-h" className="space-y-3 lg:col-start-1">
          <h2 id="requests-h" className={sectionTitle}>Your requests</h2>
          <ul className={`${card} divide-y divide-line overflow-hidden`}>
            {myRequests.slice(0, 8).map((r) => (
              <li key={r.id}>
                <Link to={r.link} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors duration-fast">
                  <RequestStatusDot status={r.status} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-fg truncate">{r.label}</span>
                    <span className="block text-xs text-fg-muted">{r.tool} · {r.detail}</span>
                  </span>
                  <Icon as={ChevronRight} className="text-fg-faint" />
                </Link>
              </li>
            ))}
          </ul>
          {myRequests.length > 8 && (
            <p className="text-xs text-fg-faint">{myRequests.length - 8} more in the tools.</p>
          )}
        </section>
      )}

      <div className="lg:col-start-2 lg:row-start-1 lg:row-span-full space-y-8 min-w-0">
        {profileState.status === 'ready' && <BalancesCard user={profileState.user} config={profileState.config} />}
        {monthBlocks.length > 0 && (
          <aside aria-labelledby="this-month-h" className={`${card} p-4 moment-in`}>
            <h2 id="this-month-h" className="text-base font-semibold text-fg">This month</h2>
            {monthBlocks.map((b, i) => (
              <div key={b.key} className={i === 0 ? 'mt-4' : 'mt-5 pt-5 border-t border-line'}>{b.node}</div>
            ))}
          </aside>
        )}
      </div>

      <div className="space-y-8 lg:col-start-1 min-w-0">
      <section aria-labelledby="tools-h" className="space-y-3">
        <h2 id="tools-h" className={sectionTitle}>Your tools</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Request-access tile leads the grid when the user has no tools. */}
          {!hasAnyAccess && <RequestAccessTile />}

          {provisionedActive.map((tool) => (
            <ActiveTile
              key={tool.slug}
              tool={applyLabelOverride(tool, labelOverrides[tool.slug])}
              onOpen={() => handleOpen(tool, navigate)}
            />
          ))}
        </div>
      </section>

      {comingSoon.length > 0 && (
        <section aria-labelledby="soon-h" className="space-y-3">
          <h2 id="soon-h" className={sectionTitle}>Coming soon</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {comingSoon.map((tool) => (
              <ComingSoonTile
                key={tool.slug}
                tool={applyLabelOverride(tool, labelOverrides[tool.slug])}
              />
            ))}
          </div>
        </section>
      )}
      </div>
      </div>
    </main>
  );
}

/** Status dot for the Your requests rows: amber waiting, green approved, red sent back. */
function RequestStatusDot({ status }: { status: MyRequestStatus }) {
  const cls = status === 'awaiting' ? 'bg-warning' : status === 'approved' ? 'bg-success' : 'bg-danger';
  const label = status === 'awaiting' ? 'Waiting' : status === 'approved' ? 'Approved' : 'Sent back';
  return <span className={`w-2 h-2 rounded-full shrink-0 ${cls}`} aria-label={label} title={label} />;
}

/**
 * Merge admin-set label overrides into a tool registry entry.
 *
 * Empty / whitespace-only overrides fall back to the registry default —
 * we want admins to be able to "clear" an override by deleting the input
 * value rather than having to remember a separate "use default" toggle.
 */
function applyLabelOverride(
  tool: ToolMetadata,
  override: { name?: string; description?: string } | null | undefined,
): ToolMetadata {
  if (!override) return tool;
  const name = override.name?.trim();
  const description = override.description?.trim();
  if (!name && !description) return tool;
  return {
    ...tool,
    name: name || tool.name,
    description: description || tool.description,
  };
}

/**
 * Decide whether to navigate within the SPA or to a different origin.
 * Internal paths (starting with "/") use react-router; absolute URLs go
 * via window.location so the whole app reloads (necessary if the URL
 * actually points to a different origin like expenses.daaily.com).
 */
function handleOpen(
  tool: ToolMetadata,
  navigate: ReturnType<typeof useNavigate>,
) {
  if (tool.url.startsWith('/')) {
    navigate(tool.url);
  } else {
    window.location.href = tool.url;
  }
}

/** 36px icon tile with the tool's Lucide icon (same as the sidebar). */
function ToolIcon({ slug }: { slug: string }) {
  return (
    <span className="w-9 h-9 rounded-lg bg-surface-2 text-fg-muted grid place-items-center shrink-0 group-hover:bg-surface-3 transition-colors duration-fast">
      <Icon as={TOOL_ICONS[slug] ?? Folder} size={20} />
    </span>
  );
}

function ActiveTile({ tool, onOpen }: { tool: ToolMetadata; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className={`${cardLink} group text-left flex items-start gap-3 p-4`}>
      <ToolIcon slug={tool.slug} />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-fg">{tool.name}</span>
        <span className="block mt-0.5 text-sm text-fg-muted line-clamp-2">{tool.description}</span>
      </span>
    </button>
  );
}

function ComingSoonTile({ tool }: { tool: ToolMetadata }) {
  return (
    <div className={`${card} flex items-start gap-3 p-4 opacity-70`}>
      <ToolIcon slug={tool.slug} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-sm font-medium text-fg-muted">
          {tool.name}
          <Icon as={Lock} size={14} className="text-fg-faint" label="Not available yet" />
        </span>
        <span className="block mt-0.5 text-sm text-fg-muted line-clamp-2">{tool.description}</span>
      </span>
    </div>
  );
}

function RequestAccessTile() {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ recipientCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const fn = httpsCallable<
        HubRequestAccessRequest,
        HubRequestAccessResponse
      >(functions, CALLABLES.HUB_REQUEST_ACCESS);
      const res = await fn({ reason: reason.trim() || undefined });
      setDone({ recipientCount: res.data.recipientCount });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not send request.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Success state: confirmation in the tile slot (same footprint, no layout shift).
  if (done) {
    return (
      <div className={`${card} flex items-start gap-3 p-4`} role="status">
        <span className="w-9 h-9 rounded-lg bg-success-subtle text-success grid place-items-center shrink-0">
          <Icon as={CircleCheck} size={20} />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium text-fg">Request sent</span>
          <span className="block mt-0.5 text-sm text-fg-muted">
            Sent to {done.recipientCount} hub admin{done.recipientCount === 1 ? '' : 's'}. They&rsquo;ll get back to you shortly.
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className={`${card} flex items-start gap-3 p-4`}>
      <span className="w-9 h-9 rounded-lg bg-brand-subtle text-brand grid place-items-center shrink-0">
        <Icon as={MailQuestion} size={20} />
      </span>
      <div className="min-w-0 flex-1 flex flex-col gap-2">
        <div>
          <p className="text-sm font-medium text-fg">Request access</p>
          <p className="mt-0.5 text-sm text-fg-muted">Tell a hub admin which tools you need.</p>
        </div>
        <label htmlFor="hub-access-reason" className="sr-only">What tools do you need?</label>
        <textarea
          id="hub-access-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="What tools do you need?"
          rows={2}
          maxLength={1000}
          className="w-full text-sm resize-none !bg-surface !text-fg !border-line-strong placeholder:!text-fg-faint"
        />
        {error && <p role="alert" className="text-xs text-danger">{error}</p>}
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={submitting}
          className={`${btnPrimary} self-start`}
        >
          {submitting ? 'Sending…' : 'Send request'}
        </button>
      </div>
    </div>
  );
}

// Re-exports for convenience — tests reach into HubLandingPage's helpers.
export type { ToolSlug };
